import _extends from "@babel/runtime/helpers/extends";
import React, { useEffect, useState } from "react";
import { AuthoritiesEnum, IntegrationTypeEnum, RoutesEnum, StatusStateEnum } from "~/types/enums";
import { Text, Grid, Spinner, Alert, Card, Box, Button, Icon } from "~/components-shared";
import { useTranslation } from "react-i18next";
import { usePostHog } from "posthog-js/react";
import { useDispatch, useSelector } from "react-redux";
import topicSelectors from "~/store/topics/selectors";
import topicActionCreators from "~/store/topics/actions";
import ErrorBoundaryCard from "~/components/ErrorBoundaryCard";
import TopicCard from "~/components/TopicCard";
import styled from "styled-components";
import Markdown from "~/components/Markdown";
import { useHistory } from "react-router-dom";
import StyledIcon from "~/components/StyledIcon";
import { TopicActionEnum } from "~/store/topics/types";
import useAbacExt from "~/hooks/abac";
import topicsActionCreators from "~/store/topics/actions";
import integrationSelectors from "~/store/integrations/selectors";
const StyledBox = styled(Box)`
  overflow: auto;
  position: relative;
  width: calc(100% + ${_ref => {
  let {
    theme
  } = _ref;
  return 26 / (theme.typography.fontSize / theme.spacing.step);
}}rem);

  &::-webkit-scrollbar {
    margin-left: 200px;
    width: 4px;
    height: 8px;
    background: transparent;
  }
  &::-webkit-scrollbar-track {
    background-color: ${_ref2 => {
  let {
    theme
  } = _ref2;
  return theme.colors.grey.main;
}};
    margin-left: ${_ref3 => {
  let {
    theme
  } = _ref3;
  return 13 / (theme.typography.fontSize / theme.spacing.step);
}}rem;
    margin-right: ${_ref4 => {
  let {
    theme
  } = _ref4;
  return 13 / (theme.typography.fontSize / theme.spacing.step);
}}rem;
  }
`;
const BoxWrapper = styled.div`
  &:after {
    content: "";
    position: absolute;
    z-index: 1;
    width: 20px;
    height: calc(100% - 10px);
    top: 0;
    right: ${_ref5 => {
  let {
    theme
  } = _ref5;
  return -13 / (theme.typography.fontSize / theme.spacing.step);
}}rem;
    background: ${_ref6 => {
  let {
    theme
  } = _ref6;
  return theme.colors.gradients.secondaryBackground(90);
}};
  }
`;
const FollowedTopicsCard = _ref7 => {
  let {
    ...rest
  } = _ref7;
  const {
    t
  } = useTranslation();
  const dispatch = useDispatch();
  const posthog = usePostHog();
  const followedStatus = useSelector(topicSelectors.getFollowedStatus);
  const topics = useSelector(topicSelectors.getFollowedTopics);
  const followedError = useSelector(topicSelectors.getFollowedError);
  const subscribed = useSelector(topicSelectors.getSubscribed);
  const subscribeStatus = useSelector(topicSelectors.getSubscribeStatus);
  const subscribeError = useSelector(topicSelectors.getSubscribeError);
  const [hoverSubscribe, setHoverSubscribe] = useState(false);
  const history = useHistory();
  const slackIntegration = useSelector(integrationSelectors.getIntegrationByType(IntegrationTypeEnum.SLACK));
  const error = followedError || subscribeError;
  const {
    userHas
  } = useAbacExt();
  useEffect(() => {
    if (followedStatus === StatusStateEnum.IDLE) {
      dispatch(topicActionCreators.fetchFollowedStarted());
    }
  }, [dispatch, followedStatus]);
  useEffect(() => {
    if (slackIntegration && subscribeStatus === StatusStateEnum.IDLE) {
      dispatch(topicsActionCreators.fetchSubscriptionStarted());
    }
  }, [dispatch, subscribeStatus, slackIntegration]);
  const handleViewAll = () => {
    history.push(RoutesEnum.TOPICS);
  };
  const handleCreate = () => {
    posthog.capture(`Followed Topics – Create a Topic`);
    dispatch(topicActionCreators.actionChanged(TopicActionEnum.PICK_TEMPLATE, null));
  };
  const handleTopicsViewAll = () => {
    posthog.capture(`Followed Topics – View all Topics`);
    history.push(RoutesEnum.TOPICS);
  };
  const handleSubscribe = () => {
    if (subscribed) {
      dispatch(topicsActionCreators.unsubscribeStarted());
      posthog.capture(`Topic Report – Unsubscribe`);
    } else {
      dispatch(topicsActionCreators.subscribeStarted());
      posthog.capture(`Topic Report – Subscribe`);
    }
  };
  return /*#__PURE__*/React.createElement(Card, _extends({}, rest, {
    title: /*#__PURE__*/React.createElement(Grid, {
      cols: 2,
      gap: 5,
      template: "auto 1fr",
      s: {
        mb: 3
      }
    }, /*#__PURE__*/React.createElement(Text, {
      element: "h2"
    }, t("TopicOverview.followed")), !!topics.length && followedStatus === StatusStateEnum.SUCCEEDED && /*#__PURE__*/React.createElement(Box, {
      direction: "row",
      justify: "end",
      gap: 3
    }, slackIntegration && /*#__PURE__*/React.createElement(Button, {
      onClick: handleSubscribe,
      onMouseEnter: () => setHoverSubscribe(true),
      onMouseLeave: () => setHoverSubscribe(false),
      loading: subscribeStatus === StatusStateEnum.LOADING,
      fontSize: 3.5,
      s: {
        pl: 4,
        pr: 6,
        py: 2
      },
      color: subscribed ? "translucent" : "primary",
      maxWidth: "150px",
      fullWidth: true
    }, /*#__PURE__*/React.createElement(Icon, {
      src: subscribed ? "tick" : "bell",
      s: {
        mr: 1,
        mt: -0.5
      }
    }), subscribed ? hoverSubscribe ? t("TopicReportInfoCard.unsubscribe") : t("TopicReportInfoCard.subscribed") : t("TopicReportInfoCard.subscribe")), userHas([AuthoritiesEnum.TOPICS_WRITE]) && /*#__PURE__*/React.createElement(Button, {
      onClick: handleCreate,
      color: "translucent",
      s: {
        pl: 4,
        pr: 6,
        py: 2
      },
      fontSize: 3.5
    }, /*#__PURE__*/React.createElement(Icon, {
      src: "plus",
      s: {
        mr: 1,
        mt: -0.5
      }
    }), t("TopicOverview.createTopic")), /*#__PURE__*/React.createElement(Button, {
      onClick: handleTopicsViewAll,
      color: "translucent",
      s: {
        pl: 6,
        pr: 4,
        py: 2
      },
      fontSize: 3.5,
      elemAfter: /*#__PURE__*/React.createElement(Icon, {
        src: "arrow",
        s: {
          mt: -0.5,
          ml: 1
        }
      })
    }, t("common.actions.viewAll"))))
  }), error && /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mb: 3
    },
    fullWidth: true
  }, error), !topics.length && followedStatus === StatusStateEnum.SUCCEEDED ? /*#__PURE__*/React.createElement(Grid, {
    cols: 1,
    justify: "center",
    s: {
      my: 8
    }
  }, /*#__PURE__*/React.createElement(StyledIcon, {
    src: "sparkles"
  }), /*#__PURE__*/React.createElement(Text, {
    element: "h3",
    weight: "semiBold",
    display: "block",
    s: {
      mt: 6
    }
  }, t("FollowedTopicsCard.noTopics")), /*#__PURE__*/React.createElement(Markdown, {
    color: "textSecondary",
    fontSize: 4,
    display: "block",
    s: {
      mt: 3,
      mb: 5
    },
    lineHeight: 6,
    style: {
      maxWidth: "500px"
    },
    align: "center",
    text: t("FollowedTopicsCard.noTopicsInfo")
  }), /*#__PURE__*/React.createElement(Box, {
    gap: 3
  }, userHas([AuthoritiesEnum.TOPICS_WRITE]) && /*#__PURE__*/React.createElement(Button, {
    onClick: handleCreate,
    s: {
      mb: 3
    }
  }, /*#__PURE__*/React.createElement(Icon, {
    src: "plus",
    scale: 5,
    s: {
      mr: 1,
      mt: -0.5
    }
  }), t("FollowedTopicsCard.createTopic")), /*#__PURE__*/React.createElement(Button, {
    onClick: handleViewAll,
    s: {
      mb: 3
    }
  }, t("FollowedTopicsCard.viewAll")))) : /*#__PURE__*/React.createElement(ErrorBoundaryCard, null, !topics.length && status === StatusStateEnum.LOADING ? /*#__PURE__*/React.createElement(Spinner, {
    s: {
      mt: 8
    },
    center: true
  }) : topics.length ? /*#__PURE__*/React.createElement(BoxWrapper, null, /*#__PURE__*/React.createElement(StyledBox, {
    wrap: "nowrap",
    align: "stretch",
    gap: 5,
    s: {
      p: 13,
      pb: 8,
      m: -13,
      mb: 0
    }
  }, topics.map(topic => /*#__PURE__*/React.createElement(TopicCard, {
    width: "23.6%",
    key: topic.id,
    topic: topic,
    shadow: true
  })))) : /*#__PURE__*/React.createElement(Text, {
    color: "textSecondary",
    fontSize: 5,
    align: "center",
    s: {
      mt: 8
    },
    display: "block"
  }, t("FollowedTopicsCard.noTopicsFound"))));
};
export default FollowedTopicsCard;